import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminSpirisProfileStatus, reportAPI } from '../../../services/api';
import { clearCachedReportTargets } from '../../auth/utils/reportTargetCache';
import { SpirisConnectionFlash, readSpirisConnectionCallbackFlash } from '../helpers/spirisConnectionSupport';

const getErrorMessage = (error: unknown, fallback: string) => {
  return error instanceof Error ? error.message : fallback;
};

type UseManageSpirisConnectionsOptions = {
  onOpenReport?: (profileKey: string) => void;
};

export const useManageSpirisConnections = ({ onOpenReport }: UseManageSpirisConnectionsOptions) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profiles, setProfiles] = useState<AdminSpirisProfileStatus[]>([]);
  const [flash, setFlash] = useState<SpirisConnectionFlash | null>(null);
  const [newProfileKey, setNewProfileKey] = useState('');
  const [allAccessGroup, setAllAccessGroup] = useState('');
  const [savedAllAccessGroup, setSavedAllAccessGroup] = useState('');
  const [allAccessUpdatedAt, setAllAccessUpdatedAt] = useState<string | null>(null);
  const [allAccessUpdatedBy, setAllAccessUpdatedBy] = useState<string | null>(null);
  const [isSavingSettings, setIsSavingSettings] = useState(false);

  const items = useMemo(
    () => profiles.slice().sort((left, right) => left.profileKey.localeCompare(right.profileKey)),
    [profiles]
  );

  const hasAllAccessGroupChanges = allAccessGroup.trim() !== savedAllAccessGroup.trim();

  const applyAllAccessSettings = useCallback((settings: { allAccessGroup?: string; updatedAt?: string; updatedBy?: string }) => {
    const resolvedAllAccessGroup = settings.allAccessGroup || '';
    setAllAccessGroup(resolvedAllAccessGroup);
    setSavedAllAccessGroup(resolvedAllAccessGroup);
    setAllAccessUpdatedAt(settings.updatedAt || null);
    setAllAccessUpdatedBy(settings.updatedBy || null);
  }, []);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [profileResponse, settings] = await Promise.all([
        reportAPI.listAdminProfiles(),
        reportAPI.getAdminAuthSettings(),
      ]);

      setProfiles(profileResponse.profiles || []);
      applyAllAccessSettings(settings);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'Kunde inte ladda.'));
    } finally {
      setIsLoading(false);
    }
  }, [applyAllAccessSettings]);

  useEffect(() => {
    const callbackFlash = readSpirisConnectionCallbackFlash();
    if (callbackFlash) {
      setFlash(callbackFlash);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const runPageAction = useCallback(async (action: () => Promise<void>, fallback: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await action();
    } catch (actionError) {
      setError(getErrorMessage(actionError, fallback));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleConnect = useCallback((profileKey: string) => {
    reportAPI.reconnectProfile(profileKey);
  }, []);

  const handleCreateProfile = useCallback(async () => {
    const profileKey = newProfileKey.trim().toLowerCase();
    if (!profileKey) {
      setError('Ange profil.');
      return;
    }

    await runPageAction(async () => {
      const created = await reportAPI.createProfile(profileKey);
      clearCachedReportTargets();
      setNewProfileKey('');
      setFlash({ severity: 'success', message: `Profil skapad (${created.profileKey}).` });
      await load();
    }, 'Kunde inte skapa.');
  }, [load, newProfileKey, runPageAction]);

  const handleSaveAllAccessGroup = useCallback(async () => {
    setIsSavingSettings(true);
    setError(null);

    try {
      const saved = await reportAPI.updateAdminAuthSettings(allAccessGroup);
      clearCachedReportTargets();
      applyAllAccessSettings(saved);
      setFlash({ severity: 'success', message: 'All-access grupp uppdaterad.' });
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'Kunde inte uppdatera.'));
    } finally {
      setIsSavingSettings(false);
    }
  }, [allAccessGroup, applyAllAccessSettings]);

  const handleDisconnect = useCallback(async (profileKey: string) => {
    if (!window.confirm(`Ta bort profil "${profileKey}" och token?`)) {
      return;
    }

    await runPageAction(async () => {
      await reportAPI.deleteProfile(profileKey);
      clearCachedReportTargets();
      setFlash({ severity: 'success', message: `Profil borttagen (${profileKey}).` });
      await load();
    }, 'Kunde inte ta bort.');
  }, [load, runPageAction]);

  const handleCreateReport = useCallback(async (profileKey: string) => {
    await runPageAction(async () => {
      await reportAPI.setProfileKey(profileKey);

      if (onOpenReport) {
        onOpenReport(profileKey);
        return;
      }

      window.history.replaceState({}, document.title, '/');
      window.location.href = '/';
    }, 'Kunde inte välja profil.');
  }, [onOpenReport, runPageAction]);

  return {
    allAccessGroup,
    allAccessUpdatedAt,
    allAccessUpdatedBy,
    error,
    flash,
    hasAllAccessGroupChanges,
    isLoading,
    isSavingSettings,
    items,
    newProfileKey,
    setAllAccessGroup,
    setFlash,
    setNewProfileKey,
    handleConnect,
    handleCreateProfile,
    handleCreateReport,
    handleDisconnect,
    handleSaveAllAccessGroup,
    load,
  };
};